import { useState, useEffect, useCallback } from 'react';

// ==================== OFFLINE DETECTION ====================

export const useOnlineStatus = () => {
  const [isOnline, setIsOnline] = useState(navigator.onLine);

  useEffect(() => {
    const handleOnline = () => {
      setIsOnline(true);
      // Trigger sync when back online
      window.dispatchEvent(new CustomEvent('app-online'));
    };
    
    const handleOffline = () => {
      setIsOnline(false);
      window.dispatchEvent(new CustomEvent('app-offline'));
    };

    window.addEventListener('online', handleOnline);
    window.addEventListener('offline', handleOffline);

    return () => {
      window.removeEventListener('online', handleOnline);
      window.removeEventListener('offline', handleOffline);
    };
  }, []);

  return isOnline;
};

// ==================== LOCAL CACHE ====================

const CACHE_PREFIX = 'dockhta_cache_';
const PENDING_ACTIONS_KEY = 'dockhta_pending_actions';
const CACHE_EXPIRY = 24 * 60 * 60 * 1000; // 24 hours

export const offlineCache = {
  // Save data to cache
  set: (key, data) => {
    try {
      const cacheItem = {
        data,
        timestamp: Date.now(),
        expiry: Date.now() + CACHE_EXPIRY
      };
      localStorage.setItem(CACHE_PREFIX + key, JSON.stringify(cacheItem));
      return true;
    } catch (error) {
      console.error('Cache set error:', error);
      return false;
    }
  },

  // Get data from cache
  get: (key) => {
    try {
      const item = localStorage.getItem(CACHE_PREFIX + key);
      if (!item) return null;
      
      const cacheItem = JSON.parse(item);
      
      // Check expiry
      if (cacheItem.expiry && Date.now() > cacheItem.expiry) {
        localStorage.removeItem(CACHE_PREFIX + key);
        return null;
      }
      
      return cacheItem.data;
    } catch (error) {
      console.error('Cache get error:', error);
      return null;
    }
  },

  // Remove from cache
  remove: (key) => {
    localStorage.removeItem(CACHE_PREFIX + key);
  },

  // Clear all cache
  clear: () => {
    const keys = Object.keys(localStorage).filter(k => k.startsWith(CACHE_PREFIX));
    keys.forEach(k => localStorage.removeItem(k));
  },

  // Get cache age
  getAge: (key) => {
    try {
      const item = localStorage.getItem(CACHE_PREFIX + key);
      if (!item) return null;
      
      const cacheItem = JSON.parse(item);
      return Date.now() - cacheItem.timestamp;
    } catch {
      return null;
    }
  }
};

// ==================== PENDING ACTIONS QUEUE ====================

export const pendingActions = {
  // Add action to queue
  add: (action) => {
    try {
      const pending = JSON.parse(localStorage.getItem(PENDING_ACTIONS_KEY) || '[]');
      const newAction = {
        id: Date.now().toString(),
        timestamp: Date.now(),
        ...action
      };
      pending.push(newAction);
      localStorage.setItem(PENDING_ACTIONS_KEY, JSON.stringify(pending));
      return newAction.id;
    } catch (error) {
      console.error('Error adding pending action:', error);
      return null;
    }
  },

  // Get all pending actions
  getAll: () => {
    try {
      return JSON.parse(localStorage.getItem(PENDING_ACTIONS_KEY) || '[]');
    } catch {
      return [];
    }
  },

  // Remove action from queue
  remove: (actionId) => {
    try {
      const pending = JSON.parse(localStorage.getItem(PENDING_ACTIONS_KEY) || '[]');
      const filtered = pending.filter(a => a.id !== actionId);
      localStorage.setItem(PENDING_ACTIONS_KEY, JSON.stringify(filtered));
    } catch (error) {
      console.error('Error removing pending action:', error);
    }
  },

  // Clear all pending actions
  clear: () => {
    localStorage.setItem(PENDING_ACTIONS_KEY, '[]');
  },

  // Get count
  count: () => {
    try {
      return JSON.parse(localStorage.getItem(PENDING_ACTIONS_KEY) || '[]').length;
    } catch {
      return 0;
    }
  }
};

// ==================== SYNC SERVICE ====================

export const syncService = {
  // Sync all pending actions when online
  syncPendingActions: async (apiCallbacks) => {
    const pending = pendingActions.getAll();
    const results = { success: 0, failed: 0, errors: [] };

    for (const action of pending) {
      try {
        const callback = apiCallbacks[action.type];
        if (callback) {
          await callback(action.data);
          pendingActions.remove(action.id);
          results.success++;
        }
      } catch (error) {
        results.failed++;
        results.errors.push({ action, error: error.message });
      }
    }

    return results;
  }
};

// ==================== HOOK FOR OFFLINE-FIRST DATA ====================

export const useOfflineData = (cacheKey, fetchFn, options = {}) => {
  const [data, setData] = useState(null);
  const [loading, setLoading] = useState(true);
  const [error, setError] = useState(null);
  const [isFromCache, setIsFromCache] = useState(false);
  const isOnline = useOnlineStatus();

  const fetchData = useCallback(async (forceRefresh = false) => {
    setLoading(true);
    setError(null);

    // Try cache first if offline or not forcing refresh
    if (!forceRefresh || !isOnline) {
      const cached = offlineCache.get(cacheKey);
      if (cached) {
        setData(cached);
        setIsFromCache(true);
        setLoading(false);
        
        // If online, fetch fresh data in background
        if (isOnline && !forceRefresh) {
          try {
            const freshData = await fetchFn();
            offlineCache.set(cacheKey, freshData);
            setData(freshData);
            setIsFromCache(false);
          } catch {
            // Keep cached data if fetch fails
          }
        }
        return;
      }
    }

    // Fetch from API if online
    if (isOnline) {
      try {
        const freshData = await fetchFn();
        offlineCache.set(cacheKey, freshData);
        setData(freshData);
        setIsFromCache(false);
      } catch (err) {
        setError(err.message);
        // Try cache as fallback
        const cached = offlineCache.get(cacheKey);
        if (cached) {
          setData(cached);
          setIsFromCache(true);
        }
      }
    } else {
      setError('Vous êtes hors ligne');
    }

    setLoading(false);
  }, [cacheKey, fetchFn, isOnline]);

  useEffect(() => {
    fetchData();
  }, [fetchData]);

  // Refresh when coming back online
  useEffect(() => {
    const handleOnline = () => fetchData(true);
    window.addEventListener('app-online', handleOnline);
    return () => window.removeEventListener('app-online', handleOnline);
  }, [fetchData]);

  return { data, loading, error, isFromCache, refresh: () => fetchData(true) };
};

export default {
  useOnlineStatus,
  offlineCache,
  pendingActions,
  syncService,
  useOfflineData
};
